import { getConfig } from './config';

export interface Override {
  testerAlias: string;
  addedTags: string[];
  removedTags: string[];
  includeInScheduling: boolean;
  priorityScore: number;
  // Free-text note explaining why the override exists. UI-only; not used by the
  // scheduling engine. Empty when unset.
  comment: string;
  // Audit trail, stamped server-side on every write. ISO 8601 UTC timestamp and
  // the alias of the user who last saved the override. Empty for legacy rows.
  lastModifiedAt: string;
  lastModifiedBy: string;
  // First-line (direct) manager login, resolved via PAPI. Empty when unknown.
  directManager: string;
  // AppSec pillar whose practice the tester belongs to (e.g.
  // 'software_services'). Empty when the tester is in no known pillar practice.
  pillar: string;
}

// The signed-in user, echoed back by GET /overrides so the UI can default the
// "only my direct reports" filter.
export interface CurrentUser {
  alias: string;
}

export interface OverridesResponse {
  overrides: Override[];
  currentUser: CurrentUser;
}

export interface Prompt {
  key: string;
  label: string;
  description: string;
  placeholders: string[];
  default: string;
  content: string;
  isDefault: boolean;
  // The pillar a per-pillar assignment-rules prompt belongs to (e.g.
  // 'software_services'). null for shared prompts (system prompt, user template).
  pillar?: string | null;
  // Whether the requesting user is allowed to edit this prompt. When false the
  // editor is rendered read-only.
  canEdit?: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface PromptVersion {
  versionId: string;
  // Human-friendly, monotonic version number (1, 2, 3, ...). May be null for
  // legacy history rows recorded before numbering existed.
  versionNumber?: number | null;
  content: string;
  isDefault: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface SchedulingThread {
  threadUuid: string;
  avpId: string;
  title: string;
  // The classified pillar, e.g. 'software_services'. Empty when unclassified.
  pillar: string;
  // Login of the pentest manager responsible for the pillar. Empty when
  // the pillar is unclassified or has no assignable practice.
  pillarManager: string;
  status: 'assigned' | 'discussion_ongoing' | 'closed_ignored' | 'assigned_externally';
  // ISO 8601 creation timestamp. Empty for legacy threads whose creation
  // time was never recorded.
  createdAt: string;
  // ISO 8601 timestamp of the last message. Empty for legacy threads
  // saved before the timestamp was recorded.
  lastMessageAt: string;
}

export interface ThreadMessage {
  role: 'user' | 'assistant';
  content: string;
  sender?: string;
  // ISO 8601 timestamp. Absent for messages saved before timestamps existed.
  ts?: string;
}

export interface ThreadDetail extends SchedulingThread {
  messages: ThreadMessage[];
}

export interface SendMessageResponse {
  response?: string;
  status: 'processing' | 'assigned' | 'discussion_ongoing' | 'closed_ignored' | 'assigned_externally';
}

export interface SchedulingResponse {
  threads: SchedulingThread[];
  total: number;
  pageSize: number;
  nextToken?: string;
  availableFilters?: {
    pillars: string[];
    managers: string[];
  };
}

async function apiCall<T>(idToken: string, method: string, path: string, body?: unknown): Promise<T> {
  const config = await getConfig();
  const resp = await fetch(config.apiUrl + path, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  // Fail loudly on non-2xx so callers can distinguish a failed request from a
  // successful-but-empty result (e.g. a 500 must not read as "no overrides").
  // The backend returns { error } on failures; surface that as the message.
  if (!resp.ok) {
    let message = `Request failed (${resp.status})`;
    try {
      const data = await resp.json();
      if (data?.error) message = data.error;
    } catch {
      // Non-JSON error body — keep the status-based fallback message.
    }
    throw new Error(message);
  }
  return resp.json();
}

export async function getOverrides(idToken: string): Promise<OverridesResponse> {
  const data = await apiCall<{ overrides?: Override[]; currentUser?: CurrentUser }>(
    idToken,
    'GET',
    '/overrides',
  );
  return {
    overrides: data.overrides ?? [],
    currentUser: data.currentUser ?? { alias: '' },
  };
}

export async function updateOverride(
  idToken: string,
  alias: string,
  // directManager/pillar are PAPI-derived enrichment; lastModifiedAt/By are
  // stamped server-side — none of them are editable.
  body: Omit<
    Override,
    'testerAlias' | 'directManager' | 'pillar' | 'lastModifiedAt' | 'lastModifiedBy'
  >,
): Promise<void> {
  await apiCall(idToken, 'PUT', `/overrides/${encodeURIComponent(alias)}`, body);
}

// Create a new override for a tester the backend validates against PAPI (real
// alias, reports to a pillar manager, not already present). Throws an Error
// whose message is the backend's reason on any non-2xx response (see
// apiCall), so the caller can show it inline.
export async function createOverride(idToken: string, alias: string, comment?: string): Promise<void> {
  await apiCall(idToken, 'POST', '/overrides', { testerAlias: alias, comment: comment ?? '' });
}

export async function getPrompts(idToken: string): Promise<Prompt[]> {
  const data = await apiCall<{ prompts?: Prompt[] }>(idToken, 'GET', '/prompts');
  return data.prompts ?? [];
}

export async function updatePrompt(idToken: string, key: string, content: string): Promise<void> {
  await apiCall(idToken, 'PUT', `/prompts/${encodeURIComponent(key)}`, { content });
}

export async function resetPrompt(idToken: string, key: string): Promise<void> {
  await apiCall(idToken, 'PUT', `/prompts/${encodeURIComponent(key)}`, { reset: true });
}

export async function getPromptHistory(idToken: string, key: string): Promise<PromptVersion[]> {
  const data = await apiCall<{ history?: PromptVersion[] }>(
    idToken,
    'GET',
    `/prompts/${encodeURIComponent(key)}/history`,
  );
  return data.history ?? [];
}

export async function getSchedulingThreads(
  idToken: string,
  opts?: { pageSize?: number; nextToken?: string; search?: string; hideAssigned?: boolean; sortBy?: string; sortOrder?: string; filterPillar?: string; filterManager?: string },
): Promise<SchedulingResponse> {
  const params = new URLSearchParams();
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize));
  if (opts?.nextToken) params.set('nextToken', opts.nextToken);
  if (opts?.search) params.set('search', opts.search);
  if (opts?.hideAssigned) params.set('hideAssigned', 'true');
  if (opts?.sortBy) params.set('sortBy', opts.sortBy);
  if (opts?.sortOrder) params.set('sortOrder', opts.sortOrder);
  if (opts?.filterPillar) params.set('filterPillar', opts.filterPillar);
  if (opts?.filterManager) params.set('filterManager', opts.filterManager);
  const qs = params.toString();
  return apiCall<SchedulingResponse>(idToken, 'GET', `/scheduling${qs ? `?${qs}` : ''}`);
}

export async function getThread(idToken: string, threadUuid: string): Promise<ThreadDetail> {
  return apiCall<ThreadDetail>(idToken, 'GET', `/scheduling/${encodeURIComponent(threadUuid)}`);
}

export async function sendMessage(idToken: string, threadUuid: string, message: string): Promise<SendMessageResponse> {
  return apiCall<SendMessageResponse>(idToken, 'POST', `/scheduling/${encodeURIComponent(threadUuid)}/message`, { message });
}

export async function markThreadAssignedExternally(
  idToken: string,
  threadUuid: string,
  note?: string,
): Promise<{ status: string; threadStatus: string }> {
  return apiCall(idToken, 'POST', `/scheduling/${encodeURIComponent(threadUuid)}/mark-assigned`, { note: note ?? '' });
}

// ── Adoption / usage stats ──

export interface StatsPerUser {
  alias: string;
  connections: number;
  interactions: number;
  dialogInteractions: number;
  schedulingInteractions: number;
  // ISO 8601 UTC timestamps of the user's first and last recorded event.
  firstSeen: string;
  lastSeen: string;
}

export interface StatsResponse {
  // ISO 8601 UTC timestamp of when the aggregation was computed.
  generatedAt: string;
  totals: {
    connections: number;
    interactions: number;
    // Distinct users with any event.
    uniqueUsers: number;
    // Distinct users with at least one interaction (engaged, not just visited).
    engagedUsers: number;
  };
  perUser: StatsPerUser[];
  connectionsOverTime: { date: string; connections: number; uniqueUsers: number }[];
  interactionsByKind: { dialog: number; scheduling: number };
}

// Record that the signed-in user opened the app. Fire-and-forget: the backend
// attributes the connection to the verified Cognito identity (the body is
// ignored) and never returns an error, so failures here are swallowed by the
// caller. Any authenticated user may record their own connection.
export async function recordSession(idToken: string): Promise<void> {
  try {
    await apiCall(idToken, 'POST', '/stats/session');
  } catch {
    // Telemetry is best-effort; never surface a failure to the user.
  }
}

// Fetch the aggregated adoption dashboard. The backend restricts this to admins
// (Federate `is_admin` claim) and returns 403 for everyone else, which apiCall
// turns into a thrown Error the caller can render.
export async function getStats(idToken: string): Promise<StatsResponse> {
  return apiCall<StatsResponse>(idToken, 'GET', '/stats');
}
